'use client';

import React, { useCallback, useRef, useEffect, useState } from 'react';
import { getClasses } from '@/_utils/classnames';
import {
  Button,
  Heading,
  Separator,
  Flex,
  Input,
  Icon,
  Text,
} from '@/_components';
import { useAdminChatThreads } from '@/_contexts/admin/AdminChatThreadsProvider';
import styles from './ChatHistoryPopup.module.css';

interface ChatHistoryPopupProps {
  isOpen: boolean;
  onClose?: () => void;
}

const groupThreadsByDate = (threads: any[]) => {
  const groups: { [key: string]: any[] } = {
    Today: [],
    Yesterday: [],
    'Last 7 Days': [],
    Older: [],
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  threads.forEach((thread: any) => {
    const threadDate = new Date(thread.updatedAt);
    if (threadDate >= today) {
      groups.Today.push(thread);
    } else if (threadDate >= yesterday) {
      groups.Yesterday.push(thread);
    } else if (threadDate >= sevenDaysAgo) {
      groups['Last 7 Days'].push(thread);
    } else {
      groups.Older.push(thread);
    }
  });

  return Object.entries(groups).filter(([, threads]) => threads.length > 0);
};

export const ChatHistoryPopup: React.FC<ChatHistoryPopupProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    isHistoryOpen,
    searchQuery,
    createNewThread,
    switchToThread,
    searchThreads,
    toggleHistory,
    getFilteredThreads,
    activeThreadId,
    deleteThread,
    updateThreadTitle,
  } = useAdminChatThreads();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const editingInputRef = useRef<HTMLInputElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const filteredThreads = getFilteredThreads();
  const groupedThreads = groupThreadsByDate(filteredThreads);

  const popupClasses: string = getClasses(styles.bottomPopup, {
    [styles.bottomPopupOpen]: isOpen || isHistoryOpen,
  });

  const overlayClasses = getClasses(styles.popupOverlay, {
    [styles.active]: isOpen || isHistoryOpen,
  });

  const handleThreadSelect = useCallback(
    (threadId: string) => {
      switchToThread(threadId);
      onClose?.();
    },
    [switchToThread, onClose],
  );

  const handleNewChat = useCallback(() => {
    createNewThread();
    onClose?.();
  }, [createNewThread, onClose]);

  const handleRenameClick = useCallback(
    (threadId: string, currentTitle: string) => {
      setEditingThreadId(threadId);
      setEditingTitle(currentTitle);
    },
    [],
  );

  const handleRenameSave = useCallback(() => {
    if (editingThreadId && editingTitle.trim()) {
      updateThreadTitle(editingThreadId, editingTitle.trim());
    }
    setEditingThreadId(null);
    setEditingTitle('');
  }, [editingThreadId, editingTitle, updateThreadTitle]);

  const handleRenameSaveSafe = useCallback(
    (e: React.FocusEvent) => {
      // Prevent saving if the focus is moving to another element in the same thread item
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (
        relatedTarget &&
        relatedTarget.closest('[data-thread-id]') ===
          e.currentTarget.closest('[data-thread-id]')
      ) {
        return;
      }
      handleRenameSave();
    },
    [handleRenameSave],
  );

  const handleRenameCancel = useCallback(() => {
    setEditingThreadId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRenameSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleRenameCancel();
      }
    },
    [handleRenameSave, handleRenameCancel],
  );

  const handleRenameInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditingTitle(e.target.value);
    },
    [],
  );

  // Handle focus management for editing input
  useEffect(() => {
    if (editingThreadId && editingInputRef.current) {
      // Use setTimeout to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        if (editingInputRef.current) {
          editingInputRef.current.focus();
          editingInputRef.current.select();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [editingThreadId]);

  // Cleanup effect to prevent DOM manipulation errors
  useEffect(() => {
    return () => {
      // Reset editing state on unmount to prevent DOM issues
      setEditingThreadId(null);
      setEditingTitle('');
    };
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      searchThreads(e.target.value);
    },
    [searchThreads],
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      // Only close if clicking the overlay itself, not its children
      if (e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [onClose],
  );

  // Reset position when popup opens
  useEffect(() => {
    if (isOpen || isHistoryOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, isHistoryOpen]);

  useEffect(() => {
    if (
      (isOpen || isHistoryOpen) &&
      searchInputRef.current &&
      popupRef.current
    ) {
      // Focus search input when popup opens
      setTimeout(() => {
        searchInputRef.current?.focus();
        // Ensure popup is focusable for keyboard navigation
        popupRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isHistoryOpen]);

  // Trap focus within the popup
  useEffect(() => {
    if (!(isOpen || isHistoryOpen)) return;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = popupRef.current?.querySelectorAll(
        'button, input, [tabindex="0"]',
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen, isHistoryOpen]);

  // Handle keyboard navigation within popup
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const totalItems = filteredThreads.length;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev < totalItems - 1 ? prev + 1 : 0;
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalItems - 1;
            return next;
          });
          break;
        case 'Enter':
          if (focusedIndex >= 0 && focusedIndex < totalItems) {
            e.preventDefault();
            handleThreadSelect(filteredThreads[focusedIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose?.();
          break;
      }
    },
    [filteredThreads, focusedIndex, handleThreadSelect, onClose],
  );

  // Handle global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'h') {
        e.preventDefault();
        toggleHistory();
      }
      if (e.key === 'Escape' && (isOpen || isHistoryOpen)) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [toggleHistory, isOpen, isHistoryOpen, onClose]);

  // Reset focused index when filtered threads change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredThreads.length, searchQuery]);

  return (
    <>
      {/* Background Overlay */}
      <div
        className={overlayClasses}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Bottom Popup Panel */}
      <div
        ref={popupRef}
        className={popupClasses}
        role="dialog"
        aria-modal="true"
        aria-label="Chat History - Browse your previous conversations"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        style={{
          transform:
            position.x !== 0 || position.y !== 0
              ? `translate(${position.x}px, ${position.y}px)`
              : undefined,
        }}
      >
        {/* Popup Header */}
        <Flex isBetween={true} paddingY={10} paddingX={16}>
          <Flex isColumn={true}>
            <Heading level={4}>Chat History</Heading>
            <Text type="small" color="secondary">
              Browse your previous conversations
            </Text>
          </Flex>
          <Button
            intent="ghost"
            iconLeft="x"
            isIconOnly
            onClick={onClose}
            aria-label="Close chat history"
            value="Close"
          />
        </Flex>
        <Separator />

        {/* Search and New Chat Controls */}
        <Flex gap={8} padding={10}>
          <Input
            label="Search"
            value={searchQuery}
            isLabelHidden={true}
            placeholder="Search conversations..."
            onChange={handleSearchChange}
          />
          <Button
            intent="primary"
            iconLeft="plus"
            value="New Chat"
            onClick={handleNewChat}
            className={styles.newChatBtn}
          />
        </Flex>
        <Separator isLight={true} />

        {/* Threads Container */}
        <Flex isColumn={true} padding={10}>
          {filteredThreads.length === 0 ? (
            <Flex
              isColumn={true}
              isCentered={true}
              padding={12}
              gap={8}
              role="status"
              aria-live="polite"
            >
              <Icon name="chats" size={32} color="secondary" />
              <Flex isColumn={true} isCentered={true}>
                <Text fontWeight="medium">
                  {searchQuery
                    ? 'No conversations found'
                    : 'No chat history yet'}
                </Text>
                <Text
                  type="small"
                  color="secondary"
                  className={styles.emptyDescription}
                >
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Start a new conversation to see it here'}
                </Text>
                {!searchQuery && (
                  <Button
                    intent="ghost"
                    iconLeft="plus"
                    value="Start chatting"
                    onClick={handleNewChat}
                    className={styles.emptyAction}
                  />
                )}
              </Flex>
            </Flex>
          ) : (
            <div role="listbox" aria-label="Chat conversations">
              {groupedThreads.map(([group, threads]) => (
                <Flex
                  isColumn={true}
                  gap={4}
                  key={group}
                  className={styles.threadGroup}
                >
                  <Text type="tiny" color="secondary" fontWeight="medium">
                    {group}
                  </Text>
                  {threads.map((thread: any, index: number) => (
                    <div
                      key={thread.id}
                      data-thread-id={thread.id}
                      className={getClasses(styles.threadItem, {
                        [styles.active]: thread.id === activeThreadId,
                        [styles.focused]: index === focusedIndex,
                      })}
                      onClick={() => handleThreadSelect(thread.id)}
                      role="option"
                      aria-selected={thread.id === activeThreadId}
                      aria-describedby={`thread-preview-${thread.id}`}
                      tabIndex={index === focusedIndex ? 0 : -1}
                      onFocus={() => setFocusedIndex(index)}
                    >
                      <div className={styles.threadContent}>
                        <div className={styles.threadHeader}>
                          {editingThreadId === thread.id ? (
                            <input
                              ref={editingInputRef}
                              type="text"
                              value={editingTitle}
                              onChange={handleRenameInputChange}
                              onKeyDown={handleRenameKeyDown}
                              onBlur={handleRenameSaveSafe}
                              className={styles.threadTitleInput}
                              maxLength={100}
                            />
                          ) : (
                            <Text
                              type="small"
                              fontWeight="medium"
                              className={styles.threadTitle}
                            >
                              {thread.title}
                            </Text>
                          )}
                          <Text
                            type="tiny"
                            color="secondary"
                            className={styles.threadTime}
                          >
                            {thread.time}
                          </Text>
                        </div>
                        <Text
                          type="small"
                          color="secondary"
                          className={styles.threadPreview}
                        >
                          <span id={`thread-preview-${thread.id}`}>
                            {thread.preview || 'New conversation'}
                          </span>
                        </Text>
                      </div>
                      <div className={styles.threadActions}>
                        {editingThreadId === thread.id ? (
                          <>
                            <Button
                              intent="ghost"
                              iconLeft="check"
                              size="small"
                              isIconOnly
                              aria-label="Save changes"
                              value="Save changes"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRenameSave();
                              }}
                            />
                            <Button
                              intent="ghost"
                              iconLeft="x"
                              size="small"
                              isIconOnly
                              aria-label="Cancel rename"
                              value="Cancel rename"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRenameCancel();
                              }}
                            />
                          </>
                        ) : (
                          <Button
                            intent="ghost"
                            iconLeft="pencil"
                            size="small"
                            isIconOnly
                            aria-label="Rename thread"
                            value="Rename thread"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameClick(thread.id, thread.title);
                            }}
                          />
                        )}
                        <Button
                          intent="ghost"
                          iconLeft="trash"
                          size="small"
                          isIconOnly
                          aria-label="Delete thread"
                          value="Delete thread"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                'Are you sure you want to delete this chat?',
                              )
                            ) {
                              deleteThread(thread.id);
                            }
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </Flex>
              ))}
            </div>
          )}
        </Flex>
      </div>
    </>
  );
};
